/**
 * The tool catalog: one source of truth for schemas, metadata, argument
 * validation and human-readable descriptions (docs/agent.md §3).
 *
 * ---------------------------------------------------------------------------
 * The resolve/apply split (the important idea in this file)
 * ---------------------------------------------------------------------------
 * A mutating tool call happens at agent time, but its op is REPLAYED much later,
 * when a human clicks Apply. Replay runs inside an immer `produce` and must be
 * synchronous and pure — it cannot make network calls, because it may run while
 * the archive is unreachable and must never block the UI on one.
 *
 * So every mutate tool has two halves:
 *
 *   resolve(args, ctx)  async, agent time. Turns the MODEL's arguments into
 *                       fully-resolved, JSON-serializable op arguments —
 *                       fetching item durations, mapping concat seconds to a
 *                       part-local range, embedding the PandoraVideoSource.
 *   apply(draft, opArgs) sync + pure, both at agent time and at apply time.
 *                       This is what the op log stores and what replay runs.
 *
 * Consequence worth remembering: **the op log holds resolved arguments, not the
 * model's**. An `add_clip {archive, item, in: 120, out: 135}` is recorded as
 * `{source: {...}, in: 12.5, out: 27.5}` with the part already chosen. Replay
 * therefore needs no archive access at all, and a proposal stays applicable
 * even if the archive is down.
 */
import type {
  JsonSchema,
  JsonSchemaProp,
  ToolDef,
  ToolKind,
  ToolResult,
} from '../core/types';
import type { Edit } from '../../format/types';
import type { EmbeddingsPort } from './embeddingsTypes';

/** Result of the pure half of a mutate tool. */
export type ApplyResult = { created: string[] } | { error: string };

/** Result of the async half: resolved op args, or an instructive error. */
export type ResolveResult =
  | { ok: true; opArgs: Record<string, unknown> }
  | { ok: false; error: string };

export interface MutateToolDef extends ToolDef {
  kind: 'mutate';
  resolve(args: Record<string, unknown>, ctx: ResolveContext): Promise<ResolveResult>;
  apply(draft: Edit, opArgs: Record<string, unknown>): ApplyResult;
}

export interface ReadToolDef extends ToolDef {
  kind: 'read';
  run(args: Record<string, unknown>, ctx: ReadContext): Promise<ToolResult>;
}

export interface PresentToolDef extends ToolDef {
  kind: 'present' | 'control';
  run(args: Record<string, unknown>, ctx: PresentContext): Promise<ToolResult>;
}

export type AnyToolDef = MutateToolDef | ReadToolDef | PresentToolDef;

/**
 * Contexts are declared here (rather than in types.ts) because they are what
 * the executors need, and keeping them next to the tool definitions is what
 * stops the two drifting apart. The concrete shapes are filled in by
 * tools/edit.ts and tools/search.ts.
 */
export interface ResolveContext {
  /** Item metadata lookup — the only archive access a mutate tool may need. */
  getItem(archive: string, item: string): Promise<ResolvedItem>;
  draft(): Edit;
  live: { playhead: number; selection: string[] };
}

export interface ResolvedItem {
  id: string;
  title: string;
  durations: number[];
  duration: number;
  posterFrame: number;
}

export interface ReadContext {
  draft(): Edit;
  live: {
    playhead: number;
    selection: string[];
    bin: { id: string; title: string; archive?: string; item?: string; in?: number; out?: number }[];
  };
  archives: { key: string; name: string }[];
  /** The archive access port — see tools/search.ts. Injected so evals can serve fixtures. */
  port: ArchivePort;
  /**
   * Post a chat message, same sink the present tools use.
   *
   * A read tool that writes to the chat looks like a category error until you
   * see what it buys: `search_moments` can hand the user every match it found
   * while handing the model only a digest. The alternative — routing the whole
   * result set through the model so it can call `show_clips` — makes the model
   * the thing that decides what to drop, on a budget far smaller than the
   * result set. That is exactly how a 47-moment search became three clips.
   */
  postChat(msg: { body: string; atts?: unknown[] }): void;
  /**
   * Semantic search over pandora-embeddings (tools/semantic.ts). Optional: absent
   * when the composer's toggle is off or no service is configured, in which case
   * the semantic tools are not in the run's tool list either — the field exists
   * so a tool that is somehow called without it can fail with an instruction
   * rather than a crash.
   */
  embeddings?: EmbeddingsPort;
}

/**
 * Everything the read tools need from pan.do/ra. A narrow port rather than an
 * injected `fetch`: fixtures are then recorded at the semantic level (items,
 * annotations, layers) instead of as HTTP bodies, which keeps them readable and
 * stable when the wire format shifts.
 */
export interface ArchivePort {
  findItems(
    archive: string,
    query: unknown,
    range: [number, number],
    sort?: { key: string; operator: '+' | '-' }[],
  ): Promise<ArchiveItem[]>;
  /**
   * Moment search. Takes a prebuilt condition tree rather than a string: the
   * boolean grammar and the item-metadata constraint are compiled in
   * tools/search.ts, so this stays a transport.
   */
  findAnnotations(
    archive: string,
    opts: AnnotationQuery & { range: [number, number] },
  ): Promise<ArchiveAnnotation[]>;
  getItem(archive: string, item: string): Promise<ArchiveItemDetail>;
  getLayers(archive: string, item: string): Promise<Record<string, ArchiveAnnotation[]>>;
  /** Queryable keys for this instance, from pandora `init`. */
  itemKeys(archive: string): Promise<string[]>;
  /** Time-aligned transcript/subtitle layer ids for this instance. */
  subtitleLayers(archive: string): Promise<string[]>;
  /** Every annotation layer on this instance, so the prompt can name them. */
  layers(archive: string): Promise<ArchiveLayer[]>;
}

/** A condition tree over annotation keys, plus the item-metadata constraint. */
export interface AnnotationQuery {
  query: unknown;
  itemsQuery?: unknown;
}

export interface ArchiveLayer {
  id: string;
  title: string;
  /** pandora layer type: 'text' | 'string' | 'place' | 'event'. */
  type?: string;
  isSubtitles?: boolean;
}

export interface ArchiveItem {
  id: string;
  title: string;
  director: string[] | null;
  year: string | number | null;
  duration: number | null;
}

export interface ArchiveItemDetail extends ArchiveItem {
  durations: number[];
  posterFrame: number | null;
}

export interface ArchiveAnnotation {
  id: string;
  in: number;
  out: number;
  value: string;
  title?: string;
  layer?: string;
}

export interface PresentContext {
  postChat(msg: { body: string; atts?: unknown[] }): void;
  proposeOps(title: string, summary: string): string;
}

// ---------------------------------------------------------------------------
// Argument validation
// ---------------------------------------------------------------------------

/**
 * Hand-rolled JSON Schema subset validator. Deliberately not ajv: the schemas
 * are small and flat (§7), a dependency-free core runs anywhere, and — most
 * importantly — the error strings are ours to write. Every message names the
 * offending field and says what shape was expected, because the model reads
 * these and is expected to correct itself from them (§3).
 */
export function validateArgs(
  schema: JsonSchema,
  args: unknown,
): { ok: true; value: Record<string, unknown> } | { ok: false; error: string } {
  if (args === null || typeof args !== 'object' || Array.isArray(args)) {
    return { ok: false, error: 'arguments must be a JSON object' };
  }
  const value = args as Record<string, unknown>;
  for (const key of schema.required ?? []) {
    if (value[key] === undefined || value[key] === null) {
      return { ok: false, error: `missing required argument "${key}"` };
    }
  }
  for (const [key, raw] of Object.entries(value)) {
    const prop = schema.properties[key];
    if (!prop) {
      const known = Object.keys(schema.properties).join(', ');
      return { ok: false, error: `unknown argument "${key}" — valid arguments are: ${known}` };
    }
    if (raw === undefined || raw === null) continue;
    const err = checkProp(key, raw, prop);
    if (err) return { ok: false, error: err };
  }
  return { ok: true, value };
}

function checkProp(path: string, raw: unknown, prop: JsonSchemaProp): string | null {
  if (prop.anyOf?.length) {
    const errors: string[] = [];
    for (const branch of prop.anyOf) {
      const err = checkProp(path, raw, branch);
      if (!err) return null;
      errors.push(err);
    }
    return `"${path}" must be one of: ${errors.map((e) => e.replace(`"${path}" must be `, '')).join('; or ')}`;
  }
  switch (prop.type) {
    case 'string':
      if (typeof raw !== 'string') return `"${path}" must be a string`;
      break;
    case 'number':
    case 'integer': {
      if (typeof raw !== 'number' || Number.isNaN(raw)) return `"${path}" must be a number`;
      if (prop.type === 'integer' && !Number.isInteger(raw))
        return `"${path}" must be a whole number`;
      if (prop.minimum !== undefined && raw < prop.minimum)
        return `"${path}" must be >= ${prop.minimum}`;
      if (prop.maximum !== undefined && raw > prop.maximum)
        return `"${path}" must be <= ${prop.maximum}`;
      break;
    }
    case 'boolean':
      if (typeof raw !== 'boolean') return `"${path}" must be true or false`;
      break;
    case 'array': {
      if (!Array.isArray(raw)) return `"${path}" must be an array`;
      if (prop.maxItems !== undefined && raw.length > prop.maxItems)
        return `"${path}" accepts at most ${prop.maxItems} items (got ${raw.length})`;
      if (prop.items) {
        for (let i = 0; i < raw.length; i++) {
          const err = checkProp(`${path}[${i}]`, raw[i], prop.items);
          if (err) return err;
        }
      }
      break;
    }
    case 'object': {
      if (raw === null || typeof raw !== 'object' || Array.isArray(raw))
        return `"${path}" must be an object`;
      const obj = raw as Record<string, unknown>;
      for (const key of prop.required ?? []) {
        if (obj[key] === undefined) return `"${path}.${key}" is required`;
      }
      if (prop.properties) {
        for (const [key, v] of Object.entries(obj)) {
          const sub = prop.properties[key];
          if (!sub) {
            const known = Object.keys(prop.properties).join(', ');
            return `unknown field "${path}.${key}" — valid fields are: ${known}`;
          }
          if (v === undefined || v === null) continue;
          const err = checkProp(`${path}.${key}`, v, sub);
          if (err) return err;
        }
      }
      break;
    }
  }
  if (prop.enum && !prop.enum.includes(raw as string | number)) {
    return `"${path}" must be one of: ${prop.enum.join(', ')}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Registry helpers
// ---------------------------------------------------------------------------

export function toolsByName(tools: AnyToolDef[]): Record<string, AnyToolDef> {
  const out: Record<string, AnyToolDef> = {};
  for (const t of tools) out[t.name] = t;
  return out;
}

/** The subset replay needs: the pure `apply` half of every mutate tool. */
export function replayRegistry(
  tools: AnyToolDef[],
): Record<string, { execute: (d: Edit, a: Record<string, unknown>) => ApplyResult; idArgPaths?: string[] }> {
  const out: Record<
    string,
    { execute: (d: Edit, a: Record<string, unknown>) => ApplyResult; idArgPaths?: string[] }
  > = {};
  for (const t of tools) {
    if (t.kind !== 'mutate') continue;
    const mutate = t as MutateToolDef;
    out[t.name] = { execute: (d, a) => mutate.apply(d, a), idArgPaths: t.idArgPaths };
  }
  return out;
}

/** OpenAI `tools` array for the request body. */
export function toolSchemas(
  tools: AnyToolDef[],
): { type: 'function'; function: unknown }[] {
  return tools.map((t) => ({
    type: 'function' as const,
    function: {
      name: t.name,
      description: t.description,
      parameters: t.schema,
    },
  }));
}

export function isMutate(tool: AnyToolDef): tool is MutateToolDef {
  return tool.kind === 'mutate';
}

export function kindOf(tool: AnyToolDef): ToolKind {
  return tool.kind;
}
